import { Injectable } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { TOAST_ICON } from '@constants/app.constants';


type ToastStatus = 'success' | 'warning' | 'error' | 'info';

interface ToastOptions {
  message: string;
  status?: ToastStatus;
  duration?: number;
  position?: 'top' | 'bottom' | 'middle';
}

@Injectable({
  providedIn: 'root',
})
export class ToastLoader {
  private readonly DEFAULT_DURATION = 4000;
  private readonly DEFAULT_POSITION: 'top' = 'top';
  private activeToast: HTMLIonToastElement | null = null;

  private readonly STATUS_MAP: Record<number, ToastStatus> = {
    200: 'success',
    201: 'success',
    204: 'success',

    400: 'warning',
    401: 'warning',
    403: 'warning',
    422: 'warning',

    404: 'error',
    405: 'error',
    500: 'error',
  };

  private readonly COLOR_MAP: Record<ToastStatus, string> = {
    success: 'dark',
    warning: 'warning',
    error: 'danger',
    info: 'primary',
  };

  constructor(private toastController: ToastController) {}

  show(code: number, message?: string) {
    const status = this.STATUS_MAP[code] || 'error';

    const fallbackMessage = this.getFallbackMessage(code);

    this.present({
      message: message || fallbackMessage,
      status,
    });
  }

  success(message: string) {
    this.present({ message, status: 'success' });
  }

  error(message: string) {
    this.present({ message, status: 'error' });
  }

  warning(message: string) {
    this.present({ message, status: 'warning' });
  }

  info(message: string) {
    this.present({ message, status: 'info' });
  }

  private async present(options: ToastOptions) {
    if (this.activeToast) {
      await this.activeToast.dismiss();
    }

    const toast = await this.toastController.create({
      message: options.message,
      color: this.COLOR_MAP[options.status || 'info'],
      position: options.position || this.DEFAULT_POSITION,
      duration: options.duration || this.DEFAULT_DURATION,
      buttons: [
        {
          icon: TOAST_ICON,
          role: 'cancel',
        },
      ],
    });

    this.activeToast = toast;

    toast.onDidDismiss().then(() => {
      this.activeToast = null;
    });

    await toast.present();
  }

  private getFallbackMessage(code: number): string {
    switch (code) {
      case 401:
        return 'Sessão expirada. Faça login novamente';
      case 403:
        return 'Você não tem permissão para esta ação';
      case 404:
        return 'Recurso não encontrado';
      case 422:
        return 'Dados inválidos';
      case 500:
        return 'Erro interno do servidor';
      default:
        return 'Ocorreu um erro inesperado';
    }
  }
}
